import React from 'react';
import { Bot, X, Calendar, User, Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { RESUME_SESSION_COPY } from '../constants/resumeSession';

interface CalendarBot {
  source: 'calendar' | 'manual';
  botSessionId: string;
  botId: string;
  eventTitle: string;
  meetingUrl: string;
  startTime: string;
  status: 'scheduled' | 'creating' | 'joining' | 'active' | 'failed' | 'terminated';
  platform: 'meet' | 'zoom' | 'teams';
  websocketUrl?: string;
  isSalespersonHost?: boolean;
}

interface ResumeSessionModalProps {
  isOpen: boolean;
  bots: CalendarBot[];
  onResume: (bot: CalendarBot) => void | Promise<void>;
  onDismiss: () => void;
  /**
   * The bot being resumed, while its join runs (it restores the call's
   * transcript and alerts before it returns): the modal stays up, says so and
   * takes no other action (owner, 2026-10-07).
   */
  resumingBotId?: string | null;
}

export const ResumeSessionModal: React.FC<ResumeSessionModalProps> = ({
  isOpen,
  bots,
  onResume,
  onDismiss,
  resumingBotId = null,
}) => {
  if (!isOpen || bots.length === 0) {
    return null;
  }
  const isResuming = resumingBotId !== null;

  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const getSourceIcon = (source: 'calendar' | 'manual') => {
    return source === 'calendar' ? <Calendar className="w-4 h-4" /> : <User className="w-4 h-4" />;
  };

  return (
    <div className="modal-backdrop">
      <div
        className="modal-panel max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        aria-busy={isResuming}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-signature to-signature-strong text-on-signature p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold text-on-signature">Resume Active Session</h2>
              <p className="text-sm text-signature-tint mt-1">
                You have {bots.length} active {bots.length === 1 ? 'session' : 'sessions'} in progress
              </p>
            </div>
          </div>
          <Button
            variant="tertiary"
            onClick={onDismiss}
            disabled={isResuming}
            className="text-on-signature hover:bg-white/10"
            aria-label={RESUME_SESSION_COPY.close}
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        {isResuming && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-3 px-6 py-4 bg-signature-tint border-b border-hairline"
          >
            <Loader2 className="w-5 h-5 animate-spin text-signature" aria-hidden="true" />
            <div>
              <p className="font-semibold text-ink">{RESUME_SESSION_COPY.resuming}</p>
              <p className="text-sm text-ink-secondary">{RESUME_SESSION_COPY.resumingDetail}</p>
            </div>
          </div>
        )}

        {/* Bot List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {bots.map((bot) => (
            <div
              key={bot.botId}
              className={`border border-hairline rounded-lg p-4 transition-all ${
                isResuming ? 'cursor-not-allowed' : 'hover:border-signature hover:bg-signature-tint cursor-pointer'
              }`}
              onClick={() => {
                if (!isResuming) onResume(bot);
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Bot Title */}
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-ink">{bot.eventTitle}</h3>
                  </div>

                  {/* Bot Details */}
                  <div className="space-y-2 text-sm text-ink-secondary">
                    <div className="flex items-center gap-2">
                      {getSourceIcon(bot.source)}
                      <span className="capitalize">{bot.source} Bot</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>Started at {formatTime(bot.startTime)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      <span className="font-mono text-xs bg-surface-subtle px-2 py-1 rounded">
                        {bot.botId}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        bot.status === 'active'
                          ? 'bg-success-tint text-success'
                          : 'bg-warning-tint text-warning'
                      }`}>
                        {bot.status}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-surface-sunken text-ink-secondary">
                        {bot.isSalespersonHost ? 'You are Host' : 'You are Participant'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Resume Button */}
                <Button
                  variant="primary"
                  className="ml-4 whitespace-nowrap"
                  disabled={isResuming}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResume(bot);
                  }}
                >
                  {resumingBotId === bot.botId ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                      {RESUME_SESSION_COPY.resuming}
                    </>
                  ) : (
                    RESUME_SESSION_COPY.resume
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-hairline p-4 bg-surface-subtle">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-secondary">
              Select a session to resume or dismiss to start a new session
            </p>
            <Button variant="tertiary" onClick={onDismiss} disabled={isResuming}>
              {RESUME_SESSION_COPY.startNew}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
